import React from 'react';
import { useSelector } from 'react-redux';
import { Card } from 'react-bootstrap';
import { Trans, useTranslation } from 'react-i18next';

import { BackButton, DownloadLinkButton, RowItem } from '@components/Base';
import { selectIsFrontline, selectIsHospital, selectIsInsurer } from '@components/Auth';
import { tInsuranceTypeName } from '@models/InsuranceType';
import { OrderBadge, OrderDocumentCardBody } from '@components/Feature/Order';
import { dUserName } from '@models/User';
import { getOrderStatusVariant, Orderable } from '@models/Order';
import { getInsurerHotlines } from '@models/Insurer';
import { dDateTimePeriod } from '@utils/datetime';

const ROW_ITEM_PROPS = {
  className: 'mb-2',
  labelProps: { xs: 7, sm: 7, md: 4, lg: 3 },
};

type Props = Orderable & {
  children: React.ReactNode;
};

const OrderableCard = ({ children, ...orderable }: Props) => {
  const { t } = useTranslation();
  const isFrontline = useSelector(selectIsFrontline);
  const isInsurer = useSelector(selectIsInsurer);
  const isHospital = useSelector(selectIsHospital);
  const {
    insuranceType,
    status,
    orderNumber,
    policyNumber,
    user,
    frontlineUser,
    insurer,
    startedAt,
    endedAt,
    coverScopeTitle,
    company,
    proof,
    note
  } = orderable;
  const statusVariant = getOrderStatusVariant(status);
  const hotline = getInsurerHotlines(insurer, insuranceType);
  const shouldShowOrderDocument = !isHospital;

  return (
    <>
      <BackButton className="mb-4" />
      <Card border={statusVariant}>
        <Card.Header as="h5" className="bg-transparent d-flex flex-row justify-content-between align-items-center">
          <span>{tInsuranceTypeName(insuranceType)}</span>
          <OrderBadge status={status} />
        </Card.Header>
        <Card.Body>
          <RowItem {...ROW_ITEM_PROPS} label={t('common:orderNumber')} value={orderNumber} />
          <RowItem
            {...ROW_ITEM_PROPS}
            label={t('common:theInsuredPolicyholder')}
            value={company ? company.name : dUserName(user)}
          />
          <RowItem {...ROW_ITEM_PROPS} label={t('common:policyNumber')} value={policyNumber} />
          <RowItem {...ROW_ITEM_PROPS} label={t('common:insurer')} value={insurer.name} />
          <RowItem {...ROW_ITEM_PROPS} label={t('common:periodOfInsurance')} value={dDateTimePeriod(startedAt, endedAt)} />
          <RowItem {...ROW_ITEM_PROPS} label={t('common:planType')} value={coverScopeTitle} />
          <RowItem {...ROW_ITEM_PROPS} label={t('common:frontlineUser')} value={dUserName(frontlineUser)} />
          {(isFrontline || isInsurer) && (
            <>
              <RowItem {...ROW_ITEM_PROPS} label={t('common:note')} value={note} />
              {proof && <DownloadLinkButton href={proof}>{t('proof')}</DownloadLinkButton>}
            </>
          )}
          <p>
            <small>
              <Trans i18nKey="common:emergencyCall" values={{ hotline }} />
            </small>
          </p>
        </Card.Body>
        {shouldShowOrderDocument && <OrderDocumentCardBody {...orderable} />}
        {children}
      </Card>
    </>
  );
};

export { OrderableCard };
